import { useState, useEffect } from 'react';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Link } from 'react-router-dom';
import Modal from '../components/Modal';

const emptyForm = { name: '', email: '', phone: '', address: '' };

export default function Clients({ userRole }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        let snapshot;
        if (userRole === 'admin') {
          snapshot = await getDocs(query(collection(db, 'clients'), orderBy('name', 'asc')));
        } else {
          snapshot = await getDocs(query(collection(db, 'clients'), where('userId', '==', auth.currentUser.uid)));
        }
        const clientList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (userRole !== 'admin') {
          clientList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }
        setClients(clientList);
      } catch (fetchError) {
        console.error('Error fetching clients:', fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [userRole]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setError(null);
    setIsModalOpen(true);
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || ''
    });
    setError(null);
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (editingId) {
        await updateDoc(doc(db, 'clients', editingId), formData);
        setClients(clients.map(c => (c.id === editingId ? { ...c, ...formData } : c)));
      } else {
        if (!auth.currentUser) throw new Error('Not authenticated');
        const docRef = await addDoc(collection(db, 'clients'), {
          ...formData,
          userId: auth.currentUser.uid,
          createdAt: serverTimestamp()
        });
        setClients([...clients, { id: docRef.id, ...formData }].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setIsModalOpen(false);
    } catch (saveError) {
      console.error('Error saving client:', saveError);
      setError(saveError.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (window.confirm(`Are you sure you want to delete ${client.name}?`)) {
      try {
        await deleteDoc(doc(db, 'clients', client.id));
        setClients(clients.filter(c => c.id !== client.id));
      } catch (deleteError) {
        console.error('Error deleting client:', deleteError);
      }
    }
  };

  if (loading) return (
    <div className="invoices-container">
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="invoices-container">
      <div className="invoices-header">
        <h1 className="invoices-title">Clients</h1>
        <button
          onClick={openCreate}
          className="create-invoice-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
          </svg>
          Add Client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16">
              <path d="M7 14s-1 5.686-1 6h2c0-.314-1-6-1-6zm4-14C9.414 0 8 1.414 8 3v3h1.21c.46.231.79.656.79 1.062V9.5A1.5 1.5 0 0 1 8.5 11H7v4h2v1h1v-2h2v-1h-2V8h2v1h1v-1.938c0-.406-.33-.831-.79-1.062A1.5 1.5 0 0 1 10.21 4H9c0-1.586.838-3 2-3V0zm3 3a1 1 0 1 0 0 2 1 1 0 0 0 0-2zM5.194 2.065c.336.233.758.226 1.03.007.12-.099.27-.08.355.048l1.24 1.387a.5.5 0 0 0 1.062-.5l-1.24-1.386a1.5 1.5 0 0 0-1.516-.584C4.9 1.674 4 2.607 4 3.5v1.805L2.53 9.66A1.5 1.5 0 0 0 3.5 12h.5v1h.5a.5.5 0 0 0 .5-.5v-1h.5v-1h-.519l2.19-3.5H3.5a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .4.203l.8 1.12L8.9 5.22A2 2 0 0 0 7 5V3.5a1 1 0 0 0-1.382-.94z"/>
            </svg>
          </div>
          <p className="empty-state-text">No clients saved yet.</p>
          <button onClick={openCreate} className="empty-state-btn">Add your first client</button>
        </div>
      ) : (
        <div className="user-table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => (
                <tr key={client.id}>
                  <td data-label="Name">
                    <span className="client-name">{client.name}</span>
                  </td>
                  <td data-label="Email">{client.email || '—'}</td>
                  <td data-label="Phone">{client.phone || '—'}</td>
                  <td data-label="Address">{client.address || '—'}</td>
                  <td className="responsive-actions-cell" data-label="Actions">
                    <div className="user-actions">
                      <Link
                        to={`/invoices?client=${encodeURIComponent(client.name)}`}
                        className="user-action-btn user-save-btn"
                      >
                        Invoices
                      </Link>
                      <button
                        onClick={() => openEdit(client)}
                        className="user-action-btn user-edit-btn"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(client)}
                        className="user-action-btn user-delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? 'Edit Client' : 'Add Client'}
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-form-group">
            <label className="modal-label">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="modal-input"
              placeholder="Client name"
            />
          </div>
          <div className="modal-form-group">
            <label className="modal-label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="modal-input"
              placeholder="client@example.com"
            />
          </div>
          <div className="modal-form-group">
            <label className="modal-label">Phone</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="modal-input"
              placeholder="Client phone"
            />
          </div>
          <div className="modal-form-group">
            <label className="modal-label">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="modal-input modal-textarea"
              rows="2"
              placeholder="Client address"
            ></textarea>
          </div>

          {error && (
            <p className="error-message" role="alert">{error}</p>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-btn modal-btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Client'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}